import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AssetListStateService, AssetListSharedState } from './asset-list-state.service';
import { AuthService } from '../../../services/auth.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AssetModelComponent } from '../../asset-model/asset-model.component';
import { MtmEditAssetModalComponent } from '../components/edit-asset-modal/edit-asset-modal.component';
import { EmitterService } from '../../../services/emitter.service';
import { Asset } from '../models/asset.model';

interface BundleResult {
  newBundle: Asset[];
  newOriginalBundle: Asset[];
}

interface SubAsset {
  id?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AssetOperationsService {
  constructor(
    private readonly stateService: AssetListStateService,
    private readonly modalService: NgbModal,
    private readonly translatePipe: TranslatePipe,
    private readonly authService: AuthService
  ) {}

  public addNewAsset(
    funnel: string,
    assetStar: string,
    bundle: Asset[],
    originalBundle: Asset[],
    projectId?: string,
    asWidget?: boolean,
    assetUpdatedCallback?: (asset: Asset) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void,
    timelineUpdateCallback?: (data: { allBundle: Asset[]; newAsset: Asset }) => void
  ): Promise<BundleResult | null> {
    const state: AssetListSharedState = this.stateService.currentState;
    const editModal: NgbModalRef = this.modalService.open(MtmEditAssetModalComponent, {
      size: 'lg',
      modalDialogClass: 'responsive-modal',
    });

    this.stateService.updateState({ editModal });
    editModal.componentInstance.funnel = funnel;

    return editModal.result.then((result: Asset) => {
      delete (result as Partial<Asset> & { affectedAsset?: unknown })?.affectedAsset;
      if (!result) {
        return null;
      }

      result.assetBundle = assetStar;
      (result as Asset & { createdBy: string }).createdBy = this.authService.getAuthUser().email;
      (result as Asset & { initial: boolean }).initial = false;

      const newOriginalBundle: Asset[] = originalBundle ? [result, ...originalBundle] : [result];
      const allBundle: Asset[] = [result, ...state.allBundle];
      const newBundle: Asset[] = [result, ...bundle];
      const allAssets: Asset[] = structuredClone(newOriginalBundle);

      this.stateService.updateState({ allBundle, allAssets });

      if (assetUpdatedCallback) {
        assetUpdatedCallback(result);
      }

      if (bundleChangeCallback) {
        bundleChangeCallback(newBundle);
      }

      if (projectId && asWidget && timelineUpdateCallback) {
        timelineUpdateCallback({ allBundle: state.allBundle, newAsset: result });
      }

      return { newBundle, newOriginalBundle };
    });
  }

  public duplicateAsset(
    content: Asset,
    assetStar: string,
    bundle: Asset[],
    originalBundle: Asset[],
    projectId?: string,
    asWidget?: boolean,
    bulkUpdateCallback?: (data: { action: string; assetIds: string[] }) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void,
    timelineUpdateCallback?: (data: { allBundle: Asset[]; newAsset: Partial<Asset>; insertAfter: string }) => void
  ): BundleResult {
    const state: AssetListSharedState = this.stateService.currentState;
    const result: Partial<Asset> = this.createDuplicatedAsset(content, assetStar);

    const newOriginalBundle: Asset[] = [...(originalBundle || [])];
    const newAllBundle: Asset[] = [...state.allBundle];
    const newBundle: Asset[] = [...(bundle || [])];

    this.insertDuplicateIntoBundle(newOriginalBundle, content.id, result);
    this.insertDuplicateIntoBundle(newAllBundle, content.id, result);
    this.insertDuplicateIntoBundle(newBundle, content.id, result);

    this.stateService.updateState({
      allBundle: [...newAllBundle],
      allAssets: [...newOriginalBundle],
    });

    if (bulkUpdateCallback) {
      bulkUpdateCallback({
        action: 'DUPLICATE',
        assetIds: [content.id || ''],
      });
    }

    if (bundleChangeCallback) {
      bundleChangeCallback(newBundle);
    }

    if (projectId && asWidget && timelineUpdateCallback) {
      timelineUpdateCallback({
        allBundle: state.allBundle,
        newAsset: result,
        insertAfter: content.id || '',
      });
    }

    return { newBundle, newOriginalBundle };
  }

  private createDuplicatedAsset(content: Asset, assetStar: string): Partial<Asset> {
    return {
      assetBundle: assetStar,
      funnel: content.funnel,
      focus: content.focus,
      type: content.type,
      detail: `Copy of ${content.detail || content.title}`,
      bets: [...(content.bets || [])],
      categories: [...((content as Asset & { categories?: string[] }).categories || [])],
      companyId: (content as Asset & { companyId?: string }).companyId,
      format: [...(content.format || [])],
      length: [...(content.length || [])],
      subAsset: ((content as Asset & { subAsset?: SubAsset[] }).subAsset || []).map((subAsset: SubAsset) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...subAssetWithoutId } = subAsset;
        return subAssetWithoutId;
      }),
      createdBy: this.authService.getAuthUser().email,
      initial: false,
      assetType: 'MASTER',
    } as Partial<Asset> & {
      categories?: string[];
      companyId?: string;
      subAsset?: Partial<SubAsset>[];
      createdBy: string;
      initial: boolean;
      assetType: string;
    };
  }

  private insertDuplicateIntoBundle(bundle: Asset[], contentId: string | undefined, result: Partial<Asset>): void {
    const index: number = bundle.findIndex((item: Asset) => item.id === contentId);
    if (index === -1) {
      bundle.push(result as Asset);
    } else {
      bundle.splice(index, 0, result as Asset);
    }
  }

  public openEditAssetModal(
    funnel: string,
    asset: Asset,
    bundle: Asset[],
    originalBundle: Asset[],
    getAllAsset: () => Asset[],
    forVariant: boolean = false,
    projectId?: string,
    asWidget?: boolean,
    assetUpdatedCallback?: (result: Asset) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void,
    timelineUpdateCallback?: (data: { allBundle: Asset[]; updatedAsset: Asset }) => void
  ): Promise<BundleResult | null> {
    const state: AssetListSharedState = this.stateService.currentState;
    const editModal: NgbModalRef = this.modalService.open(MtmEditAssetModalComponent, {
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });

    this.stateService.updateState({ editModal });
    editModal.componentInstance.asset = asset;
    editModal.componentInstance.funnel = funnel;
    editModal.componentInstance.forVariant = forVariant;
    editModal.componentInstance.affectedAssets = state.affectedAssets;

    return editModal.result.then((result: Asset & { affectedAsset?: Asset & { status: string } }) => {
      return this.handleEditModalResult(
        result,
        asset,
        bundle,
        originalBundle,
        getAllAsset,
        state,
        projectId,
        asWidget,
        assetUpdatedCallback,
        bundleChangeCallback,
        timelineUpdateCallback
      );
    });
  }

  private handleEditModalResult(
    result: Asset & { affectedAsset?: Asset & { status: string } },
    asset: Asset,
    bundle: Asset[],
    originalBundle: Asset[],
    getAllAsset: () => Asset[],
    state: { allBundle: Asset[]; affectedAssets: unknown },
    projectId?: string,
    asWidget?: boolean,
    assetUpdatedCallback?: (result: Asset) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void,
    timelineUpdateCallback?: (data: { allBundle: Asset[]; updatedAsset: Asset }) => void
  ): BundleResult | null {
    delete result?.affectedAsset;

    if (!result) {
      return null;
    }

    const isFunnelChanged: boolean = result.funnel.toLowerCase() !== asset.funnel.toLowerCase();
    const bundles: { newOriginalBundle: Asset[]; allBundle: Asset[]; newBundle: Asset[]; allAssets: Asset[] } =
      this.updateAssetInBundles(result, asset, bundle, originalBundle, getAllAsset(), state.allBundle, isFunnelChanged);

    this.stateService.updateState({
      allBundle: bundles.allBundle,
      allAssets: structuredClone(bundles.allAssets),
    });

    if (assetUpdatedCallback) {
      assetUpdatedCallback(result);
    }

    if (bundleChangeCallback) {
      bundleChangeCallback(bundles.newBundle);
    }

    if (projectId && asWidget && timelineUpdateCallback) {
      const updatedAsset: Asset = isFunnelChanged
        ? { ...asset, ...result }
        : bundles.newOriginalBundle.find((a: Asset) => a.id === asset.id) || result;

      timelineUpdateCallback({
        allBundle: state.allBundle,
        updatedAsset,
      });
    }

    return { newBundle: bundles.newBundle, newOriginalBundle: bundles.newOriginalBundle };
  }

  private updateAssetInBundles(
    result: Asset,
    asset: Asset,
    bundle: Asset[],
    originalBundle: Asset[],
    allAssets: Asset[],
    allBundle: Asset[],
    isFunnelChanged: boolean
  ): { newOriginalBundle: Asset[]; allBundle: Asset[]; newBundle: Asset[]; allAssets: Asset[] } {
    let newOriginalBundle: Asset[] = originalBundle || [];
    let updatedAllBundle: Asset[] = allBundle.length ? [...allBundle] : [];
    let newBundle: Asset[] = bundle || [];
    let updatedAllAssets: Asset[] = allAssets;

    if (isFunnelChanged) {
      const updatedAsset: Asset = { ...asset, ...result };

      newOriginalBundle = [...newOriginalBundle.filter((a: Asset) => a.id !== asset.id), updatedAsset];
      updatedAllBundle = [...updatedAllBundle.filter((a: Asset) => a.id !== asset.id), updatedAsset];
      newBundle = [...newBundle.filter((a: Asset) => a.id !== asset.id), updatedAsset];
      updatedAllAssets = [...updatedAllAssets.filter((a: Asset) => a.id !== asset.id), updatedAsset];
    } else {
      newOriginalBundle = newOriginalBundle.map((a: Asset) => (a.id === asset.id ? { ...a, ...result } : a));

      updatedAllBundle = updatedAllBundle.map((a: Asset) => (a.id === asset.id ? { ...a, ...result } : a));

      newBundle = newBundle.map((a: Asset) => (a.id === asset.id ? { ...a, ...result } : a));

      updatedAllAssets = updatedAllAssets.map((a: Asset) => (a.id === asset.id ? { ...a, ...result } : a));
    }

    return {
      newOriginalBundle,
      allBundle: updatedAllBundle,
      newBundle,
      allAssets: updatedAllAssets,
    };
  }

  public hideAsset(
    asset: Asset,
    bundle: Asset[],
    originalBundle: Asset[],
    assetUpdatedCallback?: (asset: Asset) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void
  ): BundleResult {
    const state: AssetListSharedState = this.stateService.currentState;
    const currentHidden: boolean = asset.hidden === true;
    asset.hidden = !currentHidden;

    const newOriginalBundle: Asset[] = originalBundle.map((a: Asset) => {
      if (a.id === asset.id) {
        return { ...a, hidden: asset.hidden };
      }
      return a;
    });

    let allBundle: Asset[] = [...state.allBundle];
    if (allBundle.length) {
      allBundle = allBundle.map((a: Asset) => {
        if (a.id === asset.id) {
          return { ...a, hidden: asset.hidden };
        }
        return a;
      });
    }

    const newBundle: Asset[] = bundle.map((a: Asset) => {
      if (a.id === asset.id) {
        return { ...a, hidden: asset.hidden };
      }
      return a;
    });

    this.stateService.updateState({
      allBundle,
      allAssets: structuredClone(newOriginalBundle),
    });

    if (assetUpdatedCallback) {
      assetUpdatedCallback(asset);
    }

    if (bundleChangeCallback) {
      bundleChangeCallback(newBundle);
    }

    return { newBundle, newOriginalBundle };
  }

  public deleteAsset(
    asset: Asset,
    bundle: Asset[],
    originalBundle: Asset[],
    assetDeletedCallback?: (asset: Asset) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void
  ): Promise<BundleResult | null> {
    const modalRef: NgbModalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = this.translatePipe.transform('asset_deleteAsset') || 'Delete Asset';
    modalRef.componentInstance.description = `Are you sure you want to delete the asset <strong>"${asset.detail}"</strong>?`;

    return modalRef.result
      .then((confirm: boolean) => {
        if (!confirm) {
          return null;
        }

        if (!originalBundle?.length) {
          return null;
        }

        const state: AssetListSharedState = this.stateService.currentState;
        const newOriginalBundle: Asset[] = originalBundle.filter((a: Asset) => a.id !== asset.id);
        const allBundle: Asset[] = state.allBundle?.filter((a: Asset) => a.id !== asset.id);
        const newBundle: Asset[] = bundle?.filter((a: Asset) => a.id !== asset.id);

        let selectedAssetTypes: string[] = [...state.selectedAssetTypes];
        if (selectedAssetTypes.includes(asset.id || '')) {
          selectedAssetTypes = selectedAssetTypes.filter((id: string) => id !== asset.id);
        }

        this.stateService.updateState({
          allBundle,
          allAssets: structuredClone(newOriginalBundle),
          selectedAssetTypes,
        });

        if (assetDeletedCallback) {
          assetDeletedCallback(asset);
        }

        if (bundleChangeCallback) {
          bundleChangeCallback(newBundle);
        }

        return { newBundle, newOriginalBundle };
      })
      .catch(() => null);
  }

  public addAsset(): Promise<Asset> {
    const modal: NgbModalRef = this.modalService.open(MtmEditAssetModalComponent, {
      size: 'lg',
      modalDialogClass: 'responsive-modal',
    });

    return modal.result.then((result: Asset) => {
      EmitterService.get('NEW_ASSET_ADDED').emit(result);
      return result;
    });
  }
}
